<!-- PrintConfirmModal.vue - Permission and confirmation modal before printing application -->
<template>
  <BaseModal
    :is-visible="isVisible"
    title="Confirm Print Application"
    :subtitle="application ? `Candidate: ${application.full_name} (#${application.application_id})` : ''"
    :icon="Printer"
    size="md"
    type="default"
    :show-footer="true"
    @close="handleClose"
  >
    <div class="space-y-4">
      <!-- Applicant Info Summary Card -->
      <div class="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 rounded-xl bg-secondary/10 dark:bg-secondary-dark/20 text-secondary dark:text-secondary-dark-text flex items-center justify-center font-bold text-sm">
            {{ getInitials(application?.full_name) }}
          </div>
          <div>
            <p class="font-bold text-sm text-gray-900 dark:text-white">
              {{ application?.full_name || 'Applicant Record' }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ application?.position_applied_for || 'Position Not Specified' }} • Ref #{{ application?.application_id }}
            </p>
          </div>
        </div>

        <span
          v-if="application?.application_status"
          class="px-2.5 py-0.5 rounded-full text-xs font-bold"
          :class="getStatusBadgeClass(application.application_status)"
        >
          {{ formatStatus(application.application_status) }}
        </span>
      </div>

      <!-- Permission & Confirmation Notice -->
      <div class="p-3.5 rounded-xl bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-xs text-blue-800 dark:text-blue-300 flex items-start space-x-2.5">
        <ShieldCheck class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div class="space-y-1">
          <p class="font-semibold">Confidential Document Notice</p>
          <p class="text-[11px] leading-relaxed opacity-90">
            {{ application?.record_type === 'paper'
              ? 'This document contains confidential digitized personnel records and attachments. Ensure you are authorized to print and handle confidential personnel records.'
              : 'This document contains sensitive personal information (including background checks and tax data). Ensure you are authorized to print and handle confidential personnel records.'
            }}
          </p>
        </div>
      </div>

      <!-- Social Security Number (SSN) Visibility (Online applications only) -->
      <div v-if="application?.record_type !== 'paper'" class="space-y-2 pt-1">
        <label class="block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
          Social Security Number (SSN) Display
        </label>
        <div class="grid grid-cols-2 gap-2.5">
          <!-- Mask Option -->
          <button
            type="button"
            @click="maskSsn = true"
            class="p-2.5 rounded-xl border text-left transition-all duration-150 flex items-start space-x-2.5"
            :class="maskSsn
              ? 'border-secondary dark:border-secondary-dark bg-secondary/5 dark:bg-secondary-dark/10 ring-1 ring-secondary dark:ring-secondary-dark'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800/60'"
          >
            <div class="p-1 rounded-lg mt-0.5" :class="maskSsn ? 'bg-secondary/15 text-secondary dark:text-secondary-dark-text' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'">
              <EyeOff class="w-4 h-4" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <span class="text-xs font-semibold" :class="maskSsn ? 'text-secondary dark:text-secondary-dark-text' : 'text-gray-700 dark:text-gray-300'">Mask SSN</span>
                <span class="w-2 h-2 rounded-full" :class="maskSsn ? 'bg-secondary' : 'bg-transparent'"></span>
              </div>
              <p class="text-[11px] text-gray-500 dark:text-gray-400 font-mono mt-0.5">***-**-XXXX</p>
              <p class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Protected / Hidden</p>
            </div>
          </button>

          <!-- Reveal Option -->
          <button
            type="button"
            @click="maskSsn = false"
            class="p-2.5 rounded-xl border text-left transition-all duration-150 flex items-start space-x-2.5"
            :class="!maskSsn
              ? 'border-amber-500 dark:border-amber-400 bg-amber-50/70 dark:bg-amber-950/20 ring-1 ring-amber-500 dark:ring-amber-400'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800/60'"
          >
            <div class="p-1 rounded-lg mt-0.5" :class="!maskSsn ? 'bg-amber-100 text-amber-600 dark:text-amber-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'">
              <Eye class="w-4 h-4" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <span class="text-xs font-semibold" :class="!maskSsn ? 'text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'">Reveal SSN</span>
                <span class="w-2 h-2 rounded-full" :class="!maskSsn ? 'bg-amber-500' : 'bg-transparent'"></span>
              </div>
              <p class="text-[11px] text-gray-500 dark:text-gray-400 font-mono mt-0.5">XXX-XX-XXXX</p>
              <p class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Visible / Unmasked</p>
            </div>
          </button>
        </div>
      </div>

      <!-- Additional Print Options -->
      <div class="space-y-2 pt-1">
        <label class="block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
          Additional Inclusions
        </label>
        <div class="space-y-2 text-xs">
          <label class="flex items-center space-x-2.5 text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              v-model="includeRemarks"
              class="w-4 h-4 rounded text-secondary focus:ring-secondary border-gray-300 dark:border-gray-600"
            />
            <span>Include Administrator Review Notes & Decision</span>
          </label>
          <label v-if="application?.record_type !== 'paper'" class="flex items-center space-x-2.5 text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              v-model="includeSignatures"
              class="w-4 h-4 rounded text-secondary focus:ring-secondary border-gray-300 dark:border-gray-600"
            />
            <span>Include Digital Signatures & Waivers</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #footer>
      <div class="flex items-center justify-between w-full">
        <BaseButton
          variant="outline"
          size="md"
          text="Cancel"
          @click="handleClose"
        />
        <BaseButton
          variant="primary"
          size="md"
          text="Proceed to Print"
          :icon="Printer"
          icon-position="left"
          @click="handleConfirm"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components/common'
import type { ApplicationDetail } from '@/types/application'
import { Printer, ShieldCheck, Eye, EyeOff } from 'lucide-vue-next'

interface Props {
  isVisible: boolean
  application?: ApplicationDetail | null
  initialMaskSsn?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  initialMaskSsn: true,
})

const emit = defineEmits<{
  close: []
  confirm: [options: { includeRemarks: boolean; includeSignatures: boolean; maskSsn: boolean }]
}>()

const includeRemarks = ref(true)
const includeSignatures = ref(true)
const maskSsn = ref(props.initialMaskSsn ?? true)

watch(
  () => props.isVisible,
  (val) => {
    if (val) {
      maskSsn.value = props.initialMaskSsn ?? true
    }
  }
)

watch(
  () => props.initialMaskSsn,
  (val) => {
    if (val !== undefined) {
      maskSsn.value = val
    }
  }
)

const handleClose = () => {
  emit('close')
}

const handleConfirm = () => {
  emit('confirm', {
    includeRemarks: includeRemarks.value,
    includeSignatures: includeSignatures.value,
    maskSsn: maskSsn.value,
  })
}

const getInitials = (name?: string) => {
  if (!name) return 'AP'
  const parts = name.trim().split(' ')
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const formatStatus = (status?: string) => {
  if (!status) return 'Pending'
  if (status.toLowerCase() === 'reviewed') return 'Under Review'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const getStatusBadgeClass = (status?: string) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300'
    case 'reviewed':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
    case 'pending':
    default:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
  }
}
</script>
